import { useEffect, useMemo } from 'react'
import {
  Control,
  FieldValues,
  SubmitHandler,
  useFieldArray,
  useForm,
  Controller,
} from 'react-hook-form'
import { FaFacebookF, FaInstagram, FaWhatsapp } from 'react-icons/fa'
import { FaTwitter } from 'react-icons/fa6'

import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

import useServerBusiness from '../../api/useServerBusiness'
import useServer from '../../api/useServerMain'
import AsyncComboBox from '../../components/forms/AsyncCombobox'
import Input from '../../components/forms/Input'
import TextArea from '../../components/forms/TextArea'
import Button from '../../components/misc/Button'
import PhoneInput from '@/components/forms/PhoneInput'
import SingleRadio from '@/components/forms/SingleRadio'
import Fetching from '../../components/misc/Fetching'
import { useAppSelector, useAppDispatch } from '../../store/hooks'
import { cleanObj } from '../../utils/helpers'
import { updateBusinessDetails } from '@/store/slices/initSlice'

const ContactConfig = () => {
  const { denyRoles } = useServer()

  const dispatch = useAppDispatch()

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm()
  const { fields, append, remove, insert, replace } = useFieldArray({
    control,
    name: 'phones',
  })

  const {
    fields: socialNet,
    append: appendSocialNet,
    remove: removeSocialNet,
    insert: insertSocialNet,
    replace: replaceSocialNet,
  } = useFieldArray({
    control,
    name: 'socialNetworks',
  })

  const { business, businessDetails } = useAppSelector((state) => state.init)

  const { editBusiness, isFetching } = useServerBusiness()
  type ContactFormValues = {
    [key: string]:
      | string
      | number
      | boolean
      | { id: string; type: string }
      | {
          number: string
          description: string
          codeCountry: any
          isMain: boolean
        }[]
  }

  const onSubmit: SubmitHandler<ContactFormValues> = (data) => {
    data.address = cleanObj(data.address)

    const mainIdx = Number(getValues('mainPhoneIndex'))

    const transformedPhones = (data.phones as any[]).map((phone, idx) => ({
      number: `+${phone.number.countryCode}${phone.number.phoneNumber}`,
      description: phone.description,
      codeCountry: phone.codeCountry ?? null,
      isMain: idx === mainIdx,
    }))

    data.phones = transformedPhones
    delete data.mainPhoneIndex
    editBusiness(cleanObj(data)).then(() => {
      dispatch(updateBusinessDetails(cleanObj(data)))
    })
    // editBusiness(cleanObj(data))

    removeSocialNet()
    remove()
  }

  useEffect(() => {
    if (businessDetails?.phones?.length) {
      replace(businessDetails.phones)
      const mainIdx = businessDetails.phones.findIndex((p) => p.isMain)
      if (mainIdx !== -1) setValue('mainPhoneIndex', mainIdx.toString())
    } else {
      replace([])
    }

    if (business?.socialNetworks?.length) {
      replaceSocialNet(business.socialNetworks)
    } else {
      replaceSocialNet([])
    }
  }, [businessDetails, business])

  useEffect(() => {
    const countryId = getValues('address.countryId')
    const provinceId = getValues('address.provinceId')
    const municipalityId = getValues('address.municipalityId')
    if (countryId === undefined) setValue('address.countryId', null)
    if (provinceId === undefined) setValue('address.provinceId', null)
    if (municipalityId === undefined) setValue('address.municipalityId', null)
  }, [
    watch('address.countryId'),
    watch('address.provinceId'),
    watch('address.municipalityId'),
  ])

  //manage SocialNetworks form --------------------------------------------------------------------------
  const socialNetworksTypes = [
    {
      type: 'WHATSAPP',
      icon: <FaWhatsapp className="text-2xl text-green-600" />,
      color: 'green-700',
      link: 'https://wa.me/',
    },
    {
      type: 'FACEBOOK',
      icon: <FaFacebookF className="text-2xl text-blue-800" />,
      color: 'blue-500',
      link: 'https://www.facebook.com/',
    },
    {
      type: 'TWITTER',
      icon: <FaTwitter className="text-2xl text-black" />,
      color: 'blue-500',
      link: 'https://twitter.com/',
    },
    {
      type: 'INSTAGRAM',
      icon: (
        <FaInstagram className="rounded-lg bg-gradient-to-tr from-pink-500 via-purple-500 to-indigo-500 text-2xl text-white" />
      ),
      color: 'gray-500',
      link: 'https://www.instagram.com/',
    },
  ]

  //--------------------------------------------------------------------------------
  //Default address props ---------------------------------------------
  const defaultAddress: Partial<{
    countryId: number
    defaultCountry: { id: number; name: string }
    provinceId: number
    defaultProvince: { id: number; name: string }
    defaultMunicipality: { id: number; name: string }
  }> = useMemo(() => {
    const address = business?.address
    return {
      countryId: address?.country?.id,
      defaultCountry: address?.country
        ? { id: address.country.id, name: address.country.name }
        : undefined,
      provinceId: address?.province?.id,
      defaultProvince: address?.province
        ? { id: address.province.id, name: address.province.name }
        : undefined,
      defaultMunicipality: address?.municipality
        ? { id: address.municipality.id, name: address.municipality.name }
        : undefined,
    }
  }, [business])

  console.log('fields!!!!!!!!!!!!', fields)

  //--------------------------------------------------------------------------------
  if (isFetching) return <Fetching />
  return (
    <form
      onSubmit={handleSubmit(onSubmit)} /* w-3/4 */
      className="m-auto rounded-lg bg-gray-50 p-5 shadow-md"
    >
      <div className="md:grid md:grid-cols-2 md:gap-2">
        <div className="col-span-2 pt-8">
          <div className="relative flex justify-center border-t border-slate-500">
            <span className="relative -top-3 bg-gray-50 px-5 text-center">
              Dirección
            </span>
          </div>
        </div>
        <Input
          label="Calle principal"
          name="address.street_1"
          control={control}
          defaultValue={business?.address?.street_1}
        />
        <Input
          label="Calle secundaria"
          name="address.street_2"
          control={control}
          defaultValue={business?.address?.street_2}
        />
        <Input
          label="Localidad"
          name="address.city"
          control={control}
          defaultValue={business?.address?.city}
        />
        <AsyncComboBox
          name="address.countryId"
          label="País"
          control={control}
          dataQuery={{ url: '/public/countries' }}
          normalizeData={{ id: 'id', name: 'name' }}
          defaultItem={defaultAddress?.defaultCountry}
          nullOpt={{ id: null, name: '' }}
        />
        <AsyncComboBox
          name="address.provinceId"
          label="Provincia"
          control={control}
          dataQuery={{
            url: '/public/provinces',
            defaultParams: defaultAddress.countryId
              ? { countryId: defaultAddress.countryId }
              : undefined,
          }}
          normalizeData={{ id: 'id', name: 'name' }}
          defaultItem={defaultAddress?.defaultProvince}
          dependendValue={{ countryId: watch('address.countryId') }}
          nullOpt={{ id: null, name: '' }}
        />
        <AsyncComboBox
          name="address.municipalityId"
          label="Municipio"
          control={control}
          dataQuery={{
            url: '/public/municipalities',
            defaultParams: defaultAddress?.provinceId
              ? { provinceId: defaultAddress.provinceId }
              : undefined,
          }}
          normalizeData={{ id: 'id', name: 'name' }}
          defaultItem={defaultAddress?.defaultMunicipality}
          dependendValue={{ provinceId: watch('address.provinceId') }}
          nullOpt={{ id: null, name: '' }}
        />

        <div className="col-span-2">
          <TextArea
            name="address.description"
            label="Observaciones"
            control={control}
            defaultValue={business?.address?.description}
          />
        </div>
      </div>
      {/**Teléfonos */}
      <div className="pt-8">
        <div className="relative flex justify-center border-t border-slate-500">
          <span className="relative -top-3 bg-gray-50 px-5 text-center">
            Teléfono
          </span>
        </div>
      </div>
      <Controller
        name="mainPhoneIndex"
        control={control}
        rules={{
          required: 'Debe seleccionar un teléfono principal',
        }}
        render={({ field }) => <input type="hidden" {...field} />}
      />

      {errors.mainPhoneIndex && (
        <p className="mt-2 text-sm text-red-500">
          {errors.mainPhoneIndex.message?.toString()}
        </p>
      )}

      {fields.map((item, idx) => (
        <div
          key={item.id}
          className="mt-5 flex w-full flex-col items-start gap-2 md:flex-row md:items-end"
        >
          <div className="mb-3 flex w-full flex-col items-center justify-center md:w-[10%]">
            <label className="mb-[15px] text-center text-sm font-medium leading-tight">
              Principal
            </label>
            <SingleRadio
              name="mainPhoneIndex"
              value={idx.toString()}
              control={control}
              checked={getValues('mainPhoneIndex') === idx.toString()}
              label=""
              onChangeFunction={() => {
                setValue('mainPhoneIndex', idx.toString(), {
                  shouldDirty: true,
                })
                fields.forEach((_, i) => {
                  setValue(`phones.${i}.isMain`, i === idx, {
                    shouldDirty: true,
                  })
                })
              }}
            />
          </div>
          <div className="flex w-full flex-col gap-2 md:flex-row">
            <div className="w-full md:w-[50%]">
              <PhoneInput
                name={`phones.${idx}.number`}
                label="Teléfono *"
                control={control}
                placeholder={'Teléfono'}
                valueDefault={
                  business?.phones?.[idx]?.number
                    ? {
                        phoneNumber: Number(
                          business.phones[idx].number.slice(3)
                        ),
                        countryCode: Number(
                          business.phones[idx].number.slice(0, 3)
                        ),
                      }
                    : undefined
                }
                rules={{
                  validate: (value) => {
                    if (!value || isNaN(value.phoneNumber)) {
                      return 'Llene los datos o elimine el campo'
                    }

                    const numberStr = String(value.phoneNumber).replace(
                      /\D/g,
                      ''
                    )
                    if (numberStr.length < 8) {
                      return 'El número es demasiado corto'
                    }

                    if (numberStr.length > 8) {
                      return 'El número es demasiado largo'
                    }

                    return true
                  },
                }}
              />
            </div>

            <div className="w-full md:w-[45%]">
              <Input
                name={`phones.${idx}.description`}
                control={control}
                label="Descripción"
                defaultValue={business?.phones[idx]?.description}
                rules={{ required: 'Llene los datos o elimine el campo' }}
              />
            </div>
          </div>

          <div className="pt-2 md:pt-5">
            <Button
              color="red-500"
              icon={<TrashIcon className="h-5" />}
              action={() => remove(idx)}
            />
          </div>
        </div>
      ))}

      <div className="mt-5 flex justify-end gap-2">
        <Button
          color="slate-400"
          textColor="slate-500"
          icon={<PlusIcon className="h-5" />}
          name="Insertar"
          action={() => append({})}
          full
          outline
        />
      </div>

      {/**Correo electrónico */}
      <div className="pt-8">
        <div className="relative flex justify-center border-t border-slate-500">
          <span className="relative -top-3 bg-gray-50 px-5 text-center">
            Correo Electrónico
          </span>
        </div>
      </div>

      <Input
        name="email"
        control={control}
        label="Correo Electrónico"
        defaultValue={business?.email}
      />

      {/**Redes Sociales */}
      <div className="pt-8">
        <div className="relative flex justify-center border-t border-slate-500">
          <span className="relative -top-3 bg-gray-50 px-5 text-center">
            Redes Sociales
          </span>
        </div>
      </div>

      <div className="inline-flex w-full justify-center gap-3">
        {socialNetworksTypes.map((item, idx) => (
          <div key={idx}>
            <Button
              icon={item.icon}
              color={item.color}
              outline
              action={() =>
                !getValues('socialNetworks')?.find(
                  (value: any) => item.type === value.type
                ) && appendSocialNet({ type: item.type })
              }
            />
          </div>
        ))}
      </div>

      <div className="flex w-full justify-center">
        <div className="flex flex-col">
          {socialNet.map((item, idx) => (
            <div key={item.id} className="inline-flex justify-between gap-2">
              <div key={item.id} className="flex items-center gap-2">
                {
                  socialNetworksTypes.find(
                    (itm) => itm.type === getValues('socialNetworks')[idx].type
                  )?.icon
                }
                <Input
                  name={`socialNetworks.${idx}.user`}
                  control={control}
                  defaultValue={business?.socialNetworks[idx]?.user}
                  rules={{ required: 'Llene los datos o elimine el campo' }}
                  placeholder="Usuario"
                  stackedText={
                    socialNetworksTypes.find(
                      (itm) =>
                        itm.type === getValues('socialNetworks')[idx].type
                    )?.link
                  }
                />
              </div>
              <div className="justify-cente flex items-end py-2">
                <Button
                  color="red-500"
                  icon={<TrashIcon className="h-5" />}
                  action={() => removeSocialNet(idx)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end py-5">
        <Button
          name="Actualizar"
          color="slate-600"
          type="submit"
          disabled={!denyRoles(['AUDITOR'])}
        />
      </div>
    </form>
  )
}

export default ContactConfig
